// Finnish school-grammar terms the comparisons use ("kuten suomen adessiivi"): adults often no longer
// remember them. Each entry says what the Finnish thing is and where High Valyrian differs, as the
// Kielioppi pages state it. Finnish grammar as in Iso suomen kielioppi (VISK); no HV claims of our own.
import { defineTerms } from './define';

export const terms = defineTerms({
  partitiivi: {
    fi: {
      name: 'partitiivi',
      short: '**Partitiivi** on suomen sija, joka kertoo osasta tai keskeneräisestä: *vettä*, *luin kirjaa*, *kaksi taloa*.',
      compare: 'Valyriassa partitiivia ei ole: objekti on akkusatiivissa, predikatiivi nominatiivissa ja lukusanan jälkeen substantiivi monikossa.',
      long: 'Partitiivi voi tuntua muutaman sukulaiselta, mutta muutama on luku eikä sija. Vertailussa suomen partitiivi on kuitenkin lähellä valyrian instrumentaalia: *sinun lohikäärmettäsi suurempi*.',
    },
    en: {
      name: 'partitive',
      short: 'The **partitive** is a Finnish case for a part or something unfinished: *vettä*, *luin kirjaa*, *kaksi taloa*.',
      compare: 'High Valyrian has no partitive: the object is accusative, a predicate nominative and the noun after a numeral plural.',
      long: 'The partitive may feel related to the paucal, but the paucal is a number, not a case. In comparison, though, the Finnish partitive is close to the High Valyrian instrumental: *sinun lohikäärmettäsi suurempi*.',
    },
    related: ['akkusatiivi', 'muutama', 'kokonaisobjekti'],
    firstIn: 'cases',
  },
  kokonaisobjekti: {
    fi: {
      name: 'kokonaisobjekti',
      short: '**Kokonaisobjekti** on suomen objekti, joka ei ole partitiivissa: *luin kirjan*, *näen naisen*.',
      compare: 'Valyrian akkusatiiviobjekti on lähin vastine, mutta valyriassa objekti on akkusatiivissa aina, myös kiellossa.',
    },
    en: {
      name: 'total object',
      short: 'The **total object** is a Finnish object not in the partitive: *luin kirjan*, *näen naisen*.',
      compare: 'The High Valyrian accusative object is the nearest match, but High Valyrian puts the object in the accusative always, even in negation.',
    },
    related: ['objekti', 'akkusatiivi', 'partitiivi'],
    firstIn: 'cases',
  },
  inessiivi: {
    fi: {
      name: 'inessiivi',
      short: '**Inessiivi** on suomen sisäpaikallissija *-ssa*, *-ssä*: *talossa*.',
      compare: 'Valyriassa sitä vastaa lokatiivi, joka kattaa myös adessiivin.',
    },
    en: {
      name: 'inessive',
      short: 'The **inessive** is the Finnish case for ‘in’, *-ssa*, *-ssä*: *talossa*.',
      compare: 'In High Valyrian the locative covers it, together with the adessive.',
    },
    related: ['lokatiivi', 'adessiivi'],
    firstIn: 'cases',
  },
  adessiivi: {
    fi: {
      name: 'adessiivi',
      short: '**Adessiivi** on suomen ulkopaikallissija *-lla*, *-llä*: *pöydällä*, myös välineenä *veitsellä*.',
      compare: 'Valyriassa paikka on lokatiivissa ja väline instrumentaalissa, joten suomen yksi *-lla* jakautuu kahteen sijaan.',
    },
    en: {
      name: 'adessive',
      short: 'The **adessive** is the Finnish case for ‘on, at’, *-lla*, *-llä*: *pöydällä*, also for a tool: *veitsellä*.',
      compare: 'In High Valyrian the place is locative and the tool instrumental, so the one Finnish *-lla* splits into two cases.',
    },
    related: ['lokatiivi', 'instrumentaali', 'inessiivi'],
    firstIn: 'cases',
  },
  allatiivi: {
    fi: {
      name: 'allatiivi',
      short: '**Allatiivi** on suomen sija *-lle*: *pojalle*, *pöydälle*.',
      compare: 'Valyriassa sitä vastaa useimmiten datiivi.',
    },
    en: {
      name: 'allative',
      short: 'The **allative** is the Finnish case *-lle* ‘to, onto’: *pojalle*, *pöydälle*.',
      compare: 'In High Valyrian it is usually the dative.',
    },
    related: ['datiivi', 'illatiivi'],
    firstIn: 'cases',
  },
  illatiivi: {
    fi: {
      name: 'illatiivi',
      short: '**Illatiivi** on suomen sija, joka kertoo liikkeestä johonkin sisään: *taloon*, *metsään*.',
      compare: 'Valyriassa määränpää on datiivissa; ’sisään’ ilmaistaan tarvittaessa prepositiolla.',
    },
    en: {
      name: 'illative',
      short: 'The **illative** is the Finnish case for movement into something: *taloon*, *metsään*.',
      compare: 'In High Valyrian the destination is dative; ‘into’ takes a preposition when it matters.',
    },
    related: ['datiivi', 'allatiivi', 'prepositio'],
    firstIn: 'cases',
  },
  elatiivi: {
    fi: {
      name: 'elatiivi',
      short: '**Elatiivi** on suomen sija, joka kertoo liikkeestä jonkin sisältä pois: *talosta*.',
      compare: 'Valyriassa ’jostakin ulos’ ilmaistaan prepositiolla.',
    },
    en: {
      name: 'elative',
      short: 'The **elative** is the Finnish case for movement out of something: *talosta*.',
      compare: 'In High Valyrian ‘out of’ takes a preposition.',
    },
    related: ['prepositio', 'illatiivi'],
    firstIn: 'cases',
  },
  essiivi: {
    fi: {
      name: 'essiivi',
      short: '**Essiivi** on suomen sija *-na*, *-nä*, joka kertoo olotilasta tai roolista: *lapsena*, *kuninkaana*.',
      compare: 'Valyriassa samaa ilmaisee prepositio [[hae]] ja lokatiivi, ’kuin, jonakin’.',
    },
    en: {
      name: 'essive',
      short: 'The **essive** is the Finnish case *-na*, *-nä* for a state or role: *lapsena* ‘as a child’.',
      compare: 'In High Valyrian the preposition [[hae]] with the locative says the same, ‘like, as’.',
    },
    related: ['lokatiivi', 'prepositio'],
    firstIn: 'cases',
  },
  translatiivi: {
    fi: {
      name: 'translatiivi',
      short: '**Translatiivi** on suomen sija *-ksi*, joka kertoo muutoksesta tai tuloksesta: *nimettiin kuninkaaksi*.',
      compare: 'Valyriassa nimi tai nimitys on monen verbin kanssa instrumentaalissa.',
    },
    en: {
      name: 'translative',
      short: 'The **translative** is the Finnish case *-ksi* for a change or result: *nimettiin kuninkaaksi* ‘was named king’.',
      compare: 'In High Valyrian the name or title is in the instrumental with many verbs.',
    },
    related: ['instrumentaali', 'valenssi'],
    firstIn: 'cases',
  },
  konditionaali: {
    fi: {
      name: 'konditionaali',
      short: '**Konditionaali** on suomen tapaluokka *-isi-*, joka kertoo kuvitellusta tai ehdollisesta: *tulisin*, *jos se olisi totta*.',
      compare: 'Valyriassa ei ole konditionaalia: kuviteltu ehto on subjunktiivissa, ja ’tekisin, haluaisin’ on futuurin subjunktiivi.',
    },
    en: {
      name: 'conditional',
      short: 'The **conditional** is the Finnish mood *-isi-* for the imagined or conditional: *tulisin* ‘I would come’.',
      compare: 'High Valyrian has no conditional: an imagined condition is subjunctive, and ‘I would do, I would like’ is the future subjunctive.',
    },
    related: ['subjunktiivi', 'tapaluokka'],
    firstIn: 'subjunctive',
  },
  omistusliite: {
    fi: {
      name: 'omistusliite',
      short: '**Omistusliite** on suomen pääte, joka kertoo omistajan: *talo|ni*, *talo|si*.',
      compare: 'Valyriassa omistusliitteitä ei ole: omistaja ilmaistaan omistussanalla, joka on adjektiivi.',
    },
    en: {
      name: 'possessive suffix',
      short: 'A **possessive suffix** is a Finnish ending that shows the owner: *talo|ni* ‘my house’, *talo|si* ‘your house’.',
      compare: 'High Valyrian has none: the owner is a possessive, which is an adjective.',
    },
    related: ['omistussana', 'refleksiivi'],
    firstIn: 'possessives',
  },
  astevaihtelu: {
    fi: {
      name: 'astevaihtelu',
      short: '**Astevaihtelu** on suomen *k*:n, *p*:n ja *t*:n vaihtelu sanan muodoissa: *Lahti* → *Lahden*, *kukka* → *kukan*.',
      compare: 'Valyriassakin vartalo voi muuttua päätteen edellä, esimerkiksi perfektin *t*:n edellä, mutta säännöt ovat toiset.',
    },
    en: {
      name: 'consonant gradation',
      short: '**Consonant gradation** is the Finnish alternation of *k*, *p* and *t* in a word’s forms: *Lahti* → *Lahden*, *kukka* → *kukan*.',
      compare: 'High Valyrian stems change before endings too, for example before the perfect *t*, but by other rules.',
    },
    related: ['vartalo', 'perfektivartalo'],
    firstIn: 'perfect',
  },
});
